import React, { useMemo, useRef, useState } from "react";
import { FaEraser, FaPlus, FaSearch } from "react-icons/fa";
import { AgGridReact } from "ag-grid-react";
import AgGridUtils from "../../../../utils/aggrid-utils";
import Button from "../../../../components/button/button";
import InputForm from "../../../../components/input/input-form";
import { useRuleListQuery } from "../components/use-rule-list-query";
import { Template } from "../../../../components/template/template";
import { Card } from "../../../../components/card/card";
import { navigateHook } from "../../../../hooks/use-navigate";

export default function RuleList() {
    const navigate = navigateHook();
    const [name, setName] = useState("");
    const [visibilityName, setVisibilityName] = useState("");
    const [nameInput, setNameInput] = useState("");
    const [visibilityNameInput, setVisibilityNameInput] = useState("");
    const { data, isLoading } = useRuleListQuery();

    const rows = data?.rows ?? [];

    const filteredRows = useMemo(() => {
        const n = (name || "").trim().toLowerCase();
        const v = (visibilityName || "").trim().toLowerCase();

        if (!n && !v) return rows;

        return rows.filter((r) => {
            const rowName = (r.name ?? "").toLowerCase();
            const rowVisibility = (r.visibility_name ?? "").toLowerCase();

            const okName = !n || rowName.includes(n);
            const okVisibility = !v || rowVisibility.includes(v);

            return okName && okVisibility;
        });
    }, [rows, name, visibilityName]);

    const columnDefs = useMemo(
        () => [
            {
                headerName: "Nome",
                field: "name",
                flex: 1,
                sort: "asc",
                comparator: AgGridUtils.nullLastComparator,
            },
            {
                headerName: "Visível para a função",
                field: "visibility_name",
                flex: 1,
                comparator: AgGridUtils.nullLastComparator,
            },
            {
                headerName: "Dispositivos",
                field: "devices",
                flex: 1,
                maxWidth: 200,
                valueFormatter: ({ value }) => (value == null ? 0 : value),
                comparator: AgGridUtils.nullLastComparator,
            },
        ],
        []
    );

    const gridRef = useRef(null);
    const goFirstPage = () => gridRef.current?.api?.paginationGoToFirstPage();

    const onCellClicked = ({ data, event }) => {
        if(!data?.id) return;

        let urlDev = `/rule-form/${data.id}`;
        let isBlank = !!event.ctrlKey;

        navigate(urlDev, isBlank);
    };

    const newRule = () => {
        const urlDev = "/rule-form";
        navigate(urlDev);
    };

    const handleSearch = () => {
        setName(nameInput.trim());
        setVisibilityName(visibilityNameInput.trim());
        goFirstPage();
    };

    const handleClear = () => {
        setName("");
        setVisibilityName("");
        setNameInput("");
        setVisibilityNameInput("");
        goFirstPage();
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            handleSearch();
        }
        if (event.key === "Escape") {
            handleClear();
        }
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Regras de alarmes</Template.Title>
                <Button onClick={newRule}><FaPlus />Adicionar regra</Button>
            </Template.Header>
            <Template.Body className="space-y-2 flex flex-col">
                <Card>
                    <Card.Body>
                        <div className="flex gap-4 items-end">
                            <InputForm
                                id="name"
                                label="Nome"
                                classNameWrapper="flex-1"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <InputForm
                                id="visibilityName"
                                label="Visível para a função"
                                classNameWrapper="flex-1"
                                value={visibilityNameInput}
                                onChange={(e) => setVisibilityNameInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <div className="flex gap-2">
                                <Button
                                    target="primary-outline"
                                    title="Limpar"
                                    onClick={handleClear}
                                >
                                    <FaEraser />
                                </Button>
                                <Button
                                    title="Buscar"
                                    onClick={handleSearch}
                                >
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="flex">
                            <div className="h-[calc(100vh-153px)] w-full">
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={filteredRows}
                                    columnDefs={columnDefs}
                                    pagination
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    suppressCellFocus={true}
                                    animateRows={false}
                                    onCellClicked={onCellClicked}
                                    gridOptions={{ localeText: AgGridUtils.localization() }}
                                    theme={AgGridUtils.generalTheme()}
                                />
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
}
